import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styles from './DropdownMenu.module.css';
import { transitionTime } from './MenuContents.Transition.module.css';
import RLPError from '../ErrorPage/RLPError';

export const selectionItemShape = PropTypes.shape({
  displayText: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
}).isRequired;

const propTypes = {
  closeListFn: PropTypes.func.isRequired,
  // According to the react docs refs are often null in between
  // life cycle hooks.  Therefore it is expected to have expandCollapseRef
  // as object since null and element are both objects.
  expandCollapseRef: PropTypes.shape({ current: PropTypes.object }),
  identifier: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  selectedValue: PropTypes.string.isRequired,
  selectionItems: PropTypes.arrayOf(selectionItemShape).isRequired,
  sortSelectionFn: PropTypes.func.isRequired,
  sortExpandStateFn: PropTypes.func.isRequired,
};

function OptionList(props) {
  const {
    closeListFn,
    expandCollapseRef,
    identifier,
    isExpanded,
    selectedValue,
    selectionItems,
    sortSelectionFn,
    sortExpandStateFn,
  } = props;

  const focusClass = styles['item-aria-focus'];

  const [ariaSelected, setAriaSelected] = useState(selectedValue);

  const listboxRef = useRef(null);

  useEffect(() => {
    // CSS Transitions will remount this component when transitioning away.
    // Need to check 'isExpanded' to not set focus.
    if (isExpanded) {
      listboxRef.current.focus();
    }
  }, []);

  const handleSelection = (event, itemValue, itemDisplayText) => {
    try {
      event.preventDefault();
      sortSelectionFn(false, itemValue, itemDisplayText);
    } catch (err) {
      // create an RLP error so that the error condition is logged to Datadog
      // also log the error to the console
      new RLPError({ payload: props }, err).log();
    }
  };

  const handleKeyboardNavigation = (event) => {
    const sortList = listboxRef.current;
    const currentlySelectedOption = sortList.querySelector(`.${focusClass}`);
    let newlySelectedOption = currentlySelectedOption;
    let preventDefaultAction = true;
    switch (event.key.toLowerCase()) {
      case 'tab':
        closeListFn(event);
        if (event.shiftKey) {
          expandCollapseRef.current.focus();
        } else if (!event.getModifierState()) {
          // Using 'querySelector' because CSSTransition makes updating a reference to the first card exceedingly difficult.
          const firstReport = document.querySelector(`[data-report-index='Report Index 0']`);
          if (firstReport) {
            firstReport.focus();
          }
        }
        preventDefaultAction = false;
        sortExpandStateFn(false);
        break;
      case 'enter': // (aka Select)
        handleSelection(event, currentlySelectedOption.dataset.value, currentlySelectedOption.innerText);
        expandCollapseRef.current.focus();
        break;
      case 'escape':
      case 'esc':
        closeListFn(event);
        expandCollapseRef.current.focus();
        preventDefaultAction = false;
        sortExpandStateFn(false);
        break;
      case 'end':
        newlySelectedOption = sortList.lastElementChild;
        break;
      case 'home':
        newlySelectedOption = sortList.firstElementChild;
        break;
      case 'arrowup':
        newlySelectedOption = currentlySelectedOption.previousElementSibling
          ? currentlySelectedOption.previousElementSibling
          : sortList.lastElementChild;
        break;
      case 'arrowdown':
        newlySelectedOption = currentlySelectedOption.nextElementSibling
          ? currentlySelectedOption.nextElementSibling
          : sortList.firstElementChild;
        break;
    }
    setAriaSelected(newlySelectedOption.getAttribute('data-value'));
    if (preventDefaultAction) {
      event.preventDefault();
    }
  };

  const options = selectionItems.map((item) => {
    const isSelected = item.value === selectedValue;
    const isFocused = item.value === ariaSelected;
    const itemClass = isSelected ? styles['selected-item'] : styles['unselected-item'];
    const classes = `${styles['list-item']} ${itemClass} ${isFocused ? focusClass : ''}`;
    const ariaInfo = isFocused ? { 'aria-selected': true } : {};
    return (
      <li
        id={`${identifier}-${item.value}`}
        role="option"
        onClick={(event) => handleSelection(event, item.value, item.displayText)}
        className={classes}
        data-value={item.value}
        key={item.value}
        {...ariaInfo}
      >
        {item.displayText}
      </li>
    );
  });

  return (
    <ul
      id={`${identifier}-list`}
      role="listbox"
      className={styles['selection-list']}
      aria-activedescendant={`${identifier}-${ariaSelected}`}
      aria-labelledby={`${identifier}-title`}
      ref={listboxRef}
      tabIndex="-1"
      onKeyDown={handleKeyboardNavigation}
    >
      {options}
    </ul>
  );
}

OptionList.defaultProps = {};

OptionList.propTypes = propTypes;

export default OptionList;
